import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import Webcam from "react-webcam";

import { CustomButton, SecondaryButton } from "../home/Home.sc";
import { pages } from "../pages/pages";
import { captureException } from "../sentry";
import { AiExerciseDataState, SettingsLocationState } from "../types";
import { getSettings } from "../utils/localStorage";
import { useViewportSize } from "../utils/useViewportSize";
import { Container, SectionWithSeparator, WebcamWrapper } from "./Exercise.sc";
import { ButtonContainer } from "./ExerciseForm";

const IMAGE_SIZE = 400;
const IMAGE_RESOLUTION = 512;

const videoConstraints = {
    width: {
        ideal: IMAGE_RESOLUTION,
        max: 2 * IMAGE_RESOLUTION,
    },
    height: {
        ideal: IMAGE_RESOLUTION,
        max: 2 * IMAGE_RESOLUTION,
    },
    facingMode: "environment",
};

const AiExercise: React.FunctionComponent = () => {
    const location = useLocation();
    const { settings } = (location.state as SettingsLocationState | undefined) ?? { settings: getSettings() };
    const webcamRef = React.useRef<Webcam>(null);
    const [screenshot, setScreenshot] = useState<string>();

    useEffect(() => {
        document.body.scrollTop = 0; // For Safari
        document.documentElement.scrollTop = 0; // For Chrome, Firefox, IE and Opera
        window.scrollTo({
            top: 0,
            left: 0,
            behavior: "smooth", // Optional, adds an animation
        });
    }, []);

    const { t } = useTranslation();
    const navigate = useNavigate();

    const handleTakePhotoClick = () => {
        setScreenshot(webcamRef.current?.getScreenshot() ?? undefined);
    };

    const handleRetakePhotoClick = () => {
        setScreenshot(undefined);
    };

    const handleUseThisPhoto = () => {
        if (screenshot) {
            const exerciseData: AiExerciseDataState = {
                photo: screenshot,
                settings,
            };
            navigate(pages.ai_exercise_session, { state: exerciseData });
        } else {
            captureException("No screenshot taken");
        }
    };

    const handleBack = () => {
        navigate(pages.home);
    };

    const { width } = useViewportSize();
    const imageSize = Math.min(width * 0.95, IMAGE_SIZE);

    return (
        <>
            <Container>
                <h4>Take a photo of your current surroundings</h4>
                <WebcamWrapper>
                    {screenshot && <img width={imageSize} height={imageSize} src={screenshot} />}
                    {!screenshot && (
                        <Webcam
                            forceScreenshotSourceSize
                            height={imageSize}
                            width={imageSize}
                            videoConstraints={videoConstraints}
                            screenshotFormat="image/jpeg"
                            ref={webcamRef}
                        />
                    )}
                </WebcamWrapper>
                <ButtonContainer>
                    {!screenshot && (
                        <CustomButton onClick={handleTakePhotoClick} variant="contained">
                            {t("take_photo")}
                        </CustomButton>
                    )}
                    {screenshot && (
                        <CustomButton onClick={handleRetakePhotoClick} variant="contained">
                            {t("retake_photo")}
                        </CustomButton>
                    )}
                </ButtonContainer>
                <SectionWithSeparator>
                    <ButtonContainer>
                        <CustomButton onClick={handleUseThisPhoto} variant="contained" disabled={!screenshot}>
                            {t("use_this_photo")}
                        </CustomButton>
                    </ButtonContainer>
                    <ButtonContainer>
                        <SecondaryButton onClick={handleBack} variant="contained">
                            {t("back")}
                        </SecondaryButton>
                    </ButtonContainer>
                </SectionWithSeparator>
            </Container>
        </>
    );
};

export default AiExercise;
